import axios from 'axios';
import { BACKEND_URL } from '../config_and_helpers/config';
import { showToast } from '../config_and_helpers/helpers';
import { logError, logInfo } from '../config_and_helpers/logger';
import {
  formatUploadFinalizeErrors,
  notifyUploadFinalizeFailure,
} from '../utils/uploadFinalizeErrors';
import { isCanceledRequest } from '../utils/listSearchRequest';

/**
 * Send request to finalize product image upload status.
 *
 * This function sends a POST request to the backend, passing along the final upload statuses of product images. This status is used in the backend to decide what to do with the associated `ProductImage` instances.
 *
 * The function uses Axios for sending the HTTP request. It handles both the successful and error response, and returns a corresponding message.
 *
 * @async
 * @param {string} token - The access token for the current user.
 * @param {Object} uploadStatuses - The dictionary containing the 'uploadStatus' for each image associated by the 'image_id'.
 * @returns {Promise<Object>} - An object with a boolean 'success' if request is completed; if not, it returns an array of error messages.
 * @throws Will throw an error if the request fails.
 */
export const finalizeProductImageUploadStatus = async (
  token,
  uploadStatuses
) => {
  try {
    // Send a POST request to the backend with the upload statuses.
    await axios.post(
      `${BACKEND_URL}products/update_image_upload_status/`,
      uploadStatuses,
      {
        headers: {
          Authorization: `Token ${token}`,
        },
      }
    );

    // If the request is successful, return an object with success status
    return { success: true };
  } catch (error) {
    const errPayload = error.response?.data || error.message;
    logError('product_client', 'finalizeProductImageUploadStatus failed', {
      status: error.response?.status,
    });
    const messages = formatUploadFinalizeErrors(errPayload);
    notifyUploadFinalizeFailure(messages);
    return { success: false, error: errPayload, messages };
  }
};

/**
 * Upload image files to AWS S3.
 *
 * This function handles the process of uploading supplied image files to AWS S3 through predefined, pre-signed URLs.
 * It uses Axios to send POST requests to each pre-signed URL, which are generated by the backend. The details of these URLs are packed in a FormData object along with the corresponding image file.
 * It also creates and manages promises for each upload and handles their resolutions. On successful upload, it updates the status of that upload in an 'uploadStatuses' dictionary.
 * After handling all the uploads, it returns the 'uploadStatuses' object.
 *
 * @async
 * @param {Array} presignedUrls - An array of presigned URL objects. Each object contains the S3 presigned URL details and the associated 'frontend_id'.
 * @param {Array} files - An array of file objects. Each file object contains the file data and associated 'id'.
 * @returns {Promise<Object>} - An object containing 'uploadStatuses' corresponding to each image upload.
 * @throws Will throw an error if the number of presigned URLs does not match the number of files or if any file upload fails.
 */
export const uploadImagesToS3 = async (presignedUrls, files) => {
  // Check if the number of pre-signed URLs matches the number of files
  if (presignedUrls.length !== files.length) {
    throw new Error(
      'The number of presigned URLs must match the number of files.'
    );
  }

  // Initialize the dictionary that will hold the statuses of each upload
  const uploadStatuses = {};

  // Prepare a list of upload jobs - one for each pre-signed URL/file
  const uploadPromises = presignedUrls.map(presignedPostData => {
    // Find the file to upload by matching the 'id' of the file with the 'frontend_id' in the pre-signed URL data
    const file = files.find(f => f.id === presignedPostData.frontend_id);

    // Create a new FormData object
    const formData = new FormData();

    // Loop through the pre-signed URL fields and append them to the FormData object
    // Do not append the 'image_id' field as this is not needed in the S3 request
    Object.keys(presignedPostData.fields).forEach(key => {
      if (key !== 'image_id' && key !== 'file_id') {
        formData.append(key, presignedPostData.fields[key]);
      }
    });

    // Append the file to the FormData object
    formData.append('file', file.file);

    // Make a POST request to the pre-signed URL, uploading the FormData object
    // On response (successful or not), update the upload status
    return axios.post(presignedPostData.url, formData).then(response => {
      // If the response status is successful (2xx), set the status to 'completed', otherwise 'failed'
      const finalizeId =
        presignedPostData.file_id ?? presignedPostData.image_id;
      uploadStatuses[finalizeId] =
        response.status >= 200 && response.status < 300
          ? 'completed'
          : 'failed';
      return response;
    });
  });

  // Execute all the upload jobs and wait until all of them finish
  try {
    const responses = await Promise.all(uploadPromises);

    // If any upload failed, throw an error
    responses.forEach((response, index) => {
      if (response.status < 200 || response.status >= 300) {
        throw new Error(`File ${index} upload failed: ${response.statusText}`);
      }
    });

    // Return the statuses of each upload
    return { uploadStatuses: uploadStatuses };

    // If any catch any error during upload and return it
  } catch (error) {
    // Error handled silently
    return Object.values(error.response.data);
  }
};

/**
 * Upload new image files for an existing product (presigned S3 + finalize).
 * Used by enrichment review when the user picks a local file instead of image_url.
 *
 * @async
 * @param {string} token
 * @param {number|string} productId
 * @param {Array<{ id: string, file: File }>} imageEntries
 * @returns {Promise<{success: boolean, messages?: string[]}>}
 */
export const uploadProductImageFiles = async (token, productId, imageEntries) => {
  if (!imageEntries?.length) {
    return { success: true };
  }

  const productData = {
    images: JSON.stringify(
      imageEntries.map(img => ({ id: img.id, type: img.file.type }))
    ),
  };

  try {
    logInfo('product_client', 'uploadProductImageFiles start', {
      productId,
      count: imageEntries.length,
    });

    const response = await axios.patch(
      `${BACKEND_URL}products/${productId}/`,
      productData,
      {
        headers: {
          Authorization: `Token ${token}`,
          'Content-Type': 'multipart/form-data',
        },
      }
    );

    const presignedUrls = response.data?.presigned_urls;
    if (!presignedUrls?.length) {
      logError('product_client', 'uploadProductImageFiles no presigned URLs', {
        productId,
      });
      return {
        success: false,
        messages: ['Could not prepare image upload for this product.'],
      };
    }

    const uploadResult = await uploadImagesToS3(presignedUrls, imageEntries);
    if (!uploadResult?.uploadStatuses) {
      const messages = Array.isArray(uploadResult)
        ? uploadResult.map(String)
        : ['Image upload to storage failed.'];
      logError('product_client', 'uploadProductImageFiles S3 failed', {
        productId,
        messages,
      });
      return { success: false, messages };
    }

    const finalizeRes = await finalizeProductImageUploadStatus(
      token,
      uploadResult.uploadStatuses
    );
    if (!finalizeRes.success) {
      return {
        success: false,
        messages: finalizeRes.messages || ['Failed to finalize image upload.'],
      };
    }

    logInfo('product_client', 'uploadProductImageFiles success', { productId });
    return { success: true };
  } catch (error) {
    const errPayload = error.response?.data || error.message;
    logError('product_client', 'uploadProductImageFiles failed', {
      productId,
      status: error.response?.status,
    });
    const messages = Array.isArray(errPayload)
      ? errPayload.map(String)
      : [typeof errPayload === 'string' ? errPayload : 'Image upload failed.'];
    return { success: false, messages };
  }
};

/**
 * Create a new product.
 *
 * This function is used to create a new product by sending a POST request to the backend along with the product data and images.
 * It further uses the 'uploadImagesToS3' and 'finalizeProductImageUploadStatus' functions to handle image file uploads and to update the backend about the final statuses of these uploads.
 * It shows a success toast after successful product creation.
 *
 * @async
 * @param {string} token - The access token for the current user.
 * @param {Object} productData - The data of the new product to create.
 * @param {Array} images - The array of image files to upload.
 * @returns {Promise<Object>} - An object containing the boolean 'success' if product creation is successful; if not, it returns an array of error messages.
 * @throws Will throw an error if the product creation request fails.
 */
export const createProduct = async (token, productData, images) => {
  try {
    // Make a POST request to the backend to create a new product
    const response = await axios.post(`${BACKEND_URL}products/`, productData, {
      headers: {
        Authorization: `Token ${token}`,
        'Content-Type': 'multipart/form-data',
      },
    });

    // Initialize the result object
    let result = { success: true };

    // If there are images to upload
    if (images.length) {
      const presignedUrls = response.data.presigned_urls;

      // If a response and presignedUrls exist
      if (response && presignedUrls) {
        // Upload the images to S3
        uploadImagesToS3(presignedUrls, images).then(r => {
          // If the upload was successful
          if (r && r.uploadStatuses) {
            // Finalize the image upload status
            finalizeProductImageUploadStatus(token, r.uploadStatuses).then(
              finalizeRes => {
                if (finalizeRes && !finalizeRes.success) {
                  result.success = false;
                }
              }
            );
          } else {
            // If the upload was not successful
            result.success = false;
          }
        });
      } else {
        // If no response or presignedUrls exist
        result.success = false;
      }
    }

    // Display a toast message if the product was created successfully
    result.toast = () =>
      showToast('Product created successfully!', 'success', 'top-right', 3000);

    return result;

    // Catch any errors during product creation and return them
  } catch (error) {
    // Error handled silently
    return Object.values(error.response.data);
  }
};

/**
 * Update an existing product.
 *
 * This function is used to update an existing product by sending a PATCH request to the backend along with the updated product data and new images.
 * It uses the 'uploadImagesToS3' and 'finalizeProductImageUploadStatus' functions to handle new image file uploads and to update the backend about the final statuses of these uploads.
 * It shows a success toast after successful product update.
 *
 * @async
 * @param {string} token - The access token for the current user.
 * @param {number} productId - The ID of the product to update.
 * @param {Object} productData - The updated data of the product.
 * @param {Array} newImages - The array of new image files to upload.
 * @returns {Promise<Object>} - An object containing the boolean 'success' if product update is successful; if not, it returns an array of error messages.
 * @throws Will throw an error if the product update request fails.
 */
export const updateProduct = async (
  token,
  productId,
  productData,
  newImages
) => {
  try {
    // Make a PATCH request to the backend to update the product
    const response = await axios.patch(
      `${BACKEND_URL}products/${productId}/`,
      productData,
      {
        headers: {
          Authorization: `Token ${token}`,
          'Content-Type': 'multipart/form-data',
        },
      }
    );

    // Initialize the result object
    let result = { success: true, updatedProduct: response.data };

    // If there are new images to upload
    if (newImages.length) {
      const presignedUrls = response.data.presigned_urls;

      // If a response and presignedUrls exist
      if (response && presignedUrls) {
        // Upload the images to S3
        uploadImagesToS3(presignedUrls, newImages).then(r => {
          // If the upload was successful
          if (r && r.uploadStatuses) {
            // Finalize the image upload status
            finalizeProductImageUploadStatus(token, r.uploadStatuses).then(
              finalizeRes => {
                if (finalizeRes && !finalizeRes.success) {
                  result.success = false;
                }
              }
            );
          } else {
            // If the upload was not successful
            result.success = false;
          }
        });
      } else {
        // If no response or presignedUrls exist
        result.success = false;
      }
    }

    // Display a toast message if the product update was successful
    result.toast = () =>
      showToast('Product updated successfully!', 'success', 'top-right', 3000);

    return result;

    // Catch any errors during product update and return them
  } catch (error) {
    // Error handled silently
    return Object.values(error.response.data);
  }
};

export const deleteProduct = async (token, productId) => {
  try {
    await axios.delete(`${BACKEND_URL}products/${productId}/`, {
      headers: {
        Authorization: `Token ${token}`,
      },
    });

    return {
      success: true,
      toast: () =>
        showToast(
          'Product deleted successfully!',
          'success',
          'top-right',
          3000
        ),
    };
  } catch (error) {
    // Error handled silently
    return Object.values(error.response.data);
  }
};

/**
 * Fetch and set the products.
 *
 * This function gets products details from the backend using either a new request or pagination-based request. Based on the response, it also maintains whether the end of the pagination is reached.
 * It enquires with optional parameters of `searchInput`, `supplierId`, and `supplierCatalogue` to filter the products. Appends these to the URL for managing GET queries.
 * It uses axios to send the GET request to the backend. After successful fetch, it sets the products into the passed 'setProducts' state updater function which is expected to be a React Hook 'useState' updater function.
 *
 * Each list row includes `open_quotes_count` and `open_orders_count` (non-negative integers, default 0)
 * for in-flight procurement on that product, including catalog-family variant matches.
 * List UI uses row highlight + banner in ProductTable (see productOpenProcurement.js).
 *
 * @async
 * @param {string} token - The access token for the current user.
 * @param {Function} setProducts - The state updater function to set the products.
 * @param {Object} [options={}] - The optional parameters for the request.
 * @param {string} options.searchInput - The text to search in the products.
 * @param {string} options.supplierId - The supplier ID to filter the products.
 * @param {boolean} options.supplierCatalogue - Whether to filter the products based on the supplier catalogue.
 * @param {string} options.nextPage - The URL of the next page in the pagination series.
 * @returns {Promise<Object>} - An object containing 'success', 'reachedEnd', and 'nextPage'. If fetch is successful, it returns 'success' as true. If end of the pagination is reached, it returns 'reachedEnd' as true. If there are more pages, it returns the 'nextPage' URL. If fetch fails, it returns an array of error messages.
 * @throws Will throw an error if the products fetch request fails.
 */
export const getProducts = async (token, setProducts, options = {}) => {
  const {
    searchInput = '',
    supplierId = '',
    supplierCatalogue = '',
    nextPage = null,
    category = '',
    unit = '',
    storage = '',
    currency = '',
    expiryRelevant = '',
    minPrice = '',
    maxPrice = '',
    minStock = '',
    maxStock = '',
    supplierCatalogueFilter = '',
    sortBy = '',
    sortDirection = '',
    signal = undefined,
  } = options;

  const parseBooleanQueryParam = value => {
    if (typeof value === 'boolean') {
      return value ? 'true' : 'false';
    }
    if (typeof value === 'string') {
      const normalized = value.trim().toLowerCase();
      if (normalized === 'true' || normalized === 'false') {
        return normalized;
      }
    }
    return '';
  };

  const resolvedSupplierCatalogueFilter =
    parseBooleanQueryParam(supplierCatalogueFilter) ||
    parseBooleanQueryParam(supplierCatalogue);

  // Construct the URL with the necessary parameters
  let url = nextPage ? nextPage : `${BACKEND_URL}products/?page_num=1`;

  // For subsequent pages, backend-provided nextPage is authoritative.
  // Only append filters/sort for first-page requests.
  if (!nextPage) {
    if (searchInput) {
      url += `&search=${searchInput}`;
    }
    if (supplierId) {
      url += `&supplier_id=${supplierId}`;
    }
    if (resolvedSupplierCatalogueFilter === 'true') {
      // Keep legacy compatibility for backends still relying on supplier_catalogue.
      url += `&supplier_catalogue=true`;
    }
    if (category) {
      url += `&category=${category}`;
    }
    if (unit) {
      url += `&unit=${unit}`;
    }
    if (storage) {
      url += `&storage=${storage}`;
    }
    if (currency) {
      url += `&currency=${currency}`;
    }
    if (expiryRelevant) {
      url += `&expiry_relevant=${expiryRelevant}`;
    }
    if (minPrice) {
      url += `&min_price=${minPrice}`;
    }
    if (maxPrice) {
      url += `&max_price=${maxPrice}`;
    }
    if (minStock) {
      url += `&min_stock=${minStock}`;
    }
    if (maxStock) {
      url += `&max_stock=${maxStock}`;
    }
    if (resolvedSupplierCatalogueFilter) {
      url += `&supplier_catalogue_filter=${resolvedSupplierCatalogueFilter}`;
    }
    if (sortBy) {
      url += `&sort_by=${sortBy}`;
    }
    if (sortDirection) {
      url += `&sort_direction=${sortDirection}`;
    }
  }

  try {
    // Make a GET request to the backend to fetch the products details
    const response = await axios.get(url, {
      headers: {
        Authorization: `Token ${token}`,
      },
      signal,
    });

    // Check if there is a next page in the pagination
    const nextCursor = response.data.next;

    // If there is no next page
    if (!nextCursor) {
      if (nextPage) {
        // If there is a nextPage passed, append the results to the existing products list
        setProducts(prevProducts => [
          ...prevProducts,
          ...response.data.results,
        ]);
      } else {
        // Set the received products as the products list
        setProducts(response.data.results);
      }
      return {
        success: true,
        reachedEnd: true,
        results: response.data.results || [],
      };
    }

    // If there are more pages
    if (!nextPage) {
      // Set the received products as the products list
      setProducts(response.data.results);
    } else {
      // Append the results to the existing products list
      setProducts(prevProducts => [...prevProducts, ...response.data.results]);
    }

    // Return the next page and success status
    return {
      success: true,
      nextPage: response.data.next,
      results: response.data.results || [],
    };

    // Catch any errors during fetch and return them
  } catch (error) {
    if (isCanceledRequest(error)) {
      return { canceled: true };
    }
    // Error handled silently
    return Object.values(error.response?.data ?? { error: error.message });
  }
};

/**
 * Fetch and set the details of a specific product.
 *
 * Response includes `open_quotes[]` and `open_orders[]` for in-flight procurement
 * on this product (same shape as get_details). Each row may include `match_type`
 * (`exact` | `catalog_family`) and `line_product` for volume-variant matches.
 * Quote rows: procurement status on `status`. Order rows: procurement status on
 * `quote.status` (not `order_item_status`, which is line reception).
 * Order qty: `open_orders[].received_quantity`, `open_orders[].ordered_quantity`;
 * `quantity` mirrors `received_quantity` for backward compatibility.
 * Treat missing fields as empty arrays.
 *
 * Also includes `opening_statistics` (product-level aggregated opening history).
 * See `productOpeningStatistics.js` / ProductOpeningStatisticsSection for display.
 *
 * @async
 * @param {string} token - The access token for the current user.
 * @param {number} productId - The ID of the product to get the details.
 * @param {Function} setProductDetails - The state updater function to set the product details.
 * @returns {Promise<Object>} - An object with a boolean 'success' if fetch is successful; if not, it returns an array of error messages.
 * @throws Will throw an error if the product details fetch request fails.
 */
export const getProductDetails = async (
  token,
  productId,
  setProductDetails
) => {
  try {
    // Make a GET request to the backend to fetch the product details
    const response = await axios.get(`${BACKEND_URL}products/${productId}/`, {
      headers: {
        Authorization: `Token ${token}`,
      },
    });

    // Set the received product details
    setProductDetails(response.data);

    // Return success status
    return { success: true };

    // Catch any errors during fetch and return them
  } catch (error) {
    // Error handled silently
    return Object.values(error.response.data);
  }
};

/**
 * Fetch detailed product information including competing products and stock items.
 * This function uses the get_details endpoint for lazy loading optimization.
 *
 * Response includes `open_quotes[]` and `open_orders[]` for non-fulfilled procurement
 * lines involving this product (exact or same-supplier catalog-family variant).
 * Row fields: `match_type`, `line_product` — see ProductOpenProcurementSection.
 * Status: `open_quotes[].status`; `open_orders[].quote.status` (procurement stage).
 * Order qty: `open_orders[].received_quantity`, `open_orders[].ordered_quantity`;
 * `quantity` mirrors `received_quantity` for backward compatibility.
 *
 * Also includes `opening_statistics` (product-level aggregated opening history).
 * See `productOpeningStatistics.js` / ProductOpeningStatisticsSection for display.
 *
 * @async
 * @param {string} token - The access token for the current user.
 * @param {number} productId - The ID of the product to fetch details for.
 * @param {Function} setProductDetails - The state updater function to set the detailed product data.
 * @returns {Promise<Object>} - An object with a boolean 'success' if fetch is successful; if not, it returns an array of error messages.
 * @throws Will throw an error if the product details fetch request fails.
 */
export const getProductDetailsOptimized = async (
  token,
  productId,
  setProductDetails
) => {
  try {
    // Make a GET request to the backend to fetch the detailed product information
    const response = await axios.get(
      `${BACKEND_URL}products/${productId}/get_details/`,
      {
        headers: {
          Authorization: `Token ${token}`,
        },
      }
    );

    // Set the received detailed product data
    setProductDetails(response.data);

    // Return success status
    return { success: true };

    // Catch any errors during fetch and return them
  } catch (error) {
    return {
      success: false,
      status: error.response?.status,
      messages: Object.values(
        error.response?.data || { error: 'Failed to fetch product details' }
      ),
    };
  }
};

/**
 * Fetch detailed product information and return the data directly.
 * This is a simplified version that doesn't require a state setter.
 *
 * Response shape matches getProductDetailsOptimized, including `opening_statistics`.
 * See `productOpeningStatistics.js` for field semantics.
 *
 * @async
 * @param {string} token - The access token for the current user.
 * @param {number} productId - The ID of the product to fetch details for.
 * @returns {Promise<Object>} - The product details data or null if failed.
 * @throws Will throw an error if the product details fetch request fails.
 */
export const getProductDetailsDirect = async (token, productId) => {
  try {
    // Make a GET request to the backend to fetch the detailed product information
    const response = await axios.get(
      `${BACKEND_URL}products/${productId}/get_details/`,
      {
        headers: {
          Authorization: `Token ${token}`,
        },
      }
    );

    // Return the product details data directly
    return response.data;
  } catch (error) {
    // Error handled silently
    return null;
  }
};

/**
 * Fetch and set the list of products for selection.
 *
 * This function gets the names of products from the backend for providing a selection list. It optionally supports getting the product names associated with a specific supplier. After successful fetch, it sets the names into the passed 'setProductSelectList' state updater function which is expected to be a React Hook 'useState' updater function.
 *
 * @async
 * @param {string} token - The access token for the current user.
 * @param {Function} setProductSelectList - The state updater function to set the list of product names.
 * @param {string} [supplierId] - (Optional) The supplier ID to filter the product selection list.
 * @returns {Promise<Object>} - An object with a boolean 'success' if fetch is successful; if not, it returns an array of error messages.
 * @throws Will throw an error if the product names fetch request fails.
 */
export const getProductSelectList = async (
  token,
  setProductSelectList,
  supplierId
) => {
  // Construct the URL with the necessary parameters
  let url = `${BACKEND_URL}products/names/`;
  if (supplierId) {
    url += `?supplier_id=${supplierId}`;
  }

  try {
    // Make a GET request to the backend to fetch the product names
    const response = await axios.get(url, {
      headers: {
        Authorization: `Token ${token}`,
      },
    });

    // Set the received product names as the selection list
    setProductSelectList(response.data);

    // Return success status
    return { success: true };

    // Catch any errors during fetch and return them
  } catch (error) {
    // Error handled silently
    return Object.values(error.response.data);
  }
};

/**
 * Fetch products with supplier information for project creation.
 * This function fetches products with their supplier details and formats them for dropdown selection.
 *
 * @async
 * @param {string} token - The access token for the current user.
 * @param {Function} setProductSelectList - The state updater function to set the list of products.
 * @returns {Promise<Object>} - An object with a boolean 'success' if fetch is successful; if not, it returns an array of error messages.
 * @throws Will throw an error if the product fetch request fails.
 */
export const getProductSelectListWithSuppliers = async (
  token,
  setProductSelectList
) => {
  try {
    // Fetch products with pagination - get all products for project selection
    let allProducts = [];
    let nextPage = `${BACKEND_URL}products/?page_num=1&page_size=1000`;
    let hasMore = true;

    while (hasMore) {
      const response = await axios.get(nextPage, {
        headers: {
          Authorization: `Token ${token}`,
        },
      });

      if (response.data.results) {
        allProducts = [...allProducts, ...response.data.results];
      }

      if (response.data.next) {
        nextPage = response.data.next;
      } else {
        hasMore = false;
      }
    }

    // Format products for dropdown: [{ value: id, label: "Product Name", supplier: { id, name } }]
    const formattedProducts = allProducts.map(product => {
      // Handle supplier information - could be object, ID, or missing
      let supplierInfo = { id: null, name: 'No Supplier' };
      
      if (product.supplier) {
        if (typeof product.supplier === 'object') {
          supplierInfo = {
            id: product.supplier.id || null,
            name: product.supplier.name || product.supplier_name || 'Unknown Supplier',
          };
        } else {
          // Supplier is just an ID
          supplierInfo = {
            id: product.supplier,
            name: product.supplier_name || 'Unknown Supplier',
          };
        }
      } else if (product.supplier_name) {
        supplierInfo = {
          id: product.supplier_id || null,
          name: product.supplier_name,
        };
      }

      return {
        value: product.id,
        label: product.name || product.cat_num || `Product ${product.id}`,
        supplier: supplierInfo,
      };
    });

    // Sort products by supplier name, then by product label
    formattedProducts.sort((a, b) => {
      const supplierCompare = (a.supplier.name || '').localeCompare(b.supplier.name || '');
      if (supplierCompare !== 0) return supplierCompare;
      return (a.label || '').localeCompare(b.label || '');
    });

    if (setProductSelectList) {
      setProductSelectList(formattedProducts);
    }

    return { success: true, products: formattedProducts };
  } catch (error) {
    logError('product_client', 'getProductSelectListWithSuppliers failed', error.response?.data ?? error);
    if (setProductSelectList) {
      setProductSelectList([]);
    }
    return { success: false, error: error.response?.data || error.message };
  }
};

/**
 * Check if a product catalog number is unique.
 *
 * This function takes a catalog number as input and checks against the backend whether this number is unique among the products (i.e., no other product has this catalog number).
 * It uses axios to send a GET request to the backend with the catalog number as a query parameter.
 *
 * @async
 * @param {string} token - The access token for the current user.
 * @param {string} value - The product catalog number to check for uniqueness.
 * @returns {Promise<boolean>} - The uniqueness of the product catalog number. If the fetch is not successful, it returns an array of error messages.
 * @throws Will throw an error if the check request fails.
 */
export const checkCatNum = async (token, value) => {
  try {
    // Make a GET request to the backend to check the uniqueness of the catalog number
    const response = await axios.get(
      `${BACKEND_URL}products/check_cat_num/?value=${value}`,
      {
        headers: {
          Authorization: `Token ${token}`,
        },
      }
    );

    // Return the uniqueness of the catalog number
    return response.data.unique;

    // Catch any errors during check and return them
  } catch (error) {
    // Error handled silently
    return Object.values(error.response.data);
  }
};

const DEFAULT_UPDATE_PRODUCT_STOCK_TOAST =
  'Product stock updated successfully!';

/**
 * Toast copy for `POST /products/update_product_stock/`.
 * Kit re-sync ignores the requested value and returns `stock_derived_from_components`;
 * prefer the backend `message` in that case.
 *
 * @param {Object} [data]
 * @returns {string}
 */
export const resolveUpdateProductStockToastMessage = (data = {}) => {
  if (
    data.stock_derived_from_components &&
    typeof data.message === 'string' &&
    data.message.trim()
  ) {
    return data.message.trim();
  }
  return DEFAULT_UPDATE_PRODUCT_STOCK_TOAST;
};

/**
 * Map `POST /products/update_product_stock/` JSON to the FE client shape.
 * Always surfaces kit-derived fields when the backend sends them. Empty
 * `updated_items` (kit re-sync) is kept as an array — callers must not treat
 * it as “replace all lots with none” unless they intend to.
 *
 * @param {Object} [data]
 * @returns {Object}
 */
export const mapUpdateProductStockResponse = (data = {}) => {
  const result = {
    success: true,
    stockDerivedFromComponents: Boolean(data.stock_derived_from_components),
  };

  if (data.stock !== undefined) result.stock = data.stock;
  if (data.effective_stock !== undefined) {
    result.effectiveStock = data.effective_stock;
  }
  if (data.stock_difference !== undefined) {
    result.stockDifference = data.stock_difference;
  }
  if (data.message !== undefined) result.message = data.message;

  if (Array.isArray(data.updated_items)) {
    result.updatedItems = data.updated_items;
  } else if (
    Array.isArray(data.new_items) &&
    data.new_items.length === 0
  ) {
    result.stockEmpty = true;
  } else {
    result.newItems = data.new_items;
  }

  return result;
};

/**
 * Update the stock level of a specific product.
 *
 * This function is used to update the stock level of a product by sending a POST request to the backend along with the updated stock value and the product ID.
 * It shows a success toast after successful product stock update.
 *
 * For kits (`category === 'Kit'`), the backend ignores `value` and re-syncs
 * package count from BOM components. Treat response `stock` as derived; do
 * not treat a typed stepper value as authoritative.
 *
 * @async
 * @param {string} token - The access token for the current user.
 * @param {number} productId - The ID of the product whose stock is to be updated.
 * @param {number} value - The updated stock level (ignored for kits).
 * @param {object} product - The product object (optional, used for multi-component logic).
 * @returns {Promise<Object>} - An object containing the boolean 'success' and a 'toast' function that, when called, shows a success toast. If not successful, it returns an array of error messages.
 * @throws Will throw an error if the product stock update request fails.
 */
export const updateProductStock = async (
  token,
  productId,
  value,
  product = null
) => {
  try {
    const response = await axios.post(
      `${BACKEND_URL}products/update_product_stock/`,
      {
        value: parseInt(value),
        product_id: productId,
      },
      {
        headers: {
          Authorization: `Token ${token}`,
        },
      }
    );

    const data = response.data || {};
    const return_object = mapUpdateProductStockResponse(data);
    const toastMessage = resolveUpdateProductStockToastMessage(data);
    return_object.toast = () =>
      showToast(toastMessage, 'success', 'top-right', 3000);

    logInfo('product_client', 'updateProductStock success', {
      productId,
      requestedValue: parseInt(value, 10),
      stock: return_object.stock,
      effectiveStock: return_object.effectiveStock,
      stockDerivedFromComponents: return_object.stockDerivedFromComponents,
      stockDifference: return_object.stockDifference,
      updatedItemCount: Array.isArray(return_object.updatedItems)
        ? return_object.updatedItems.length
        : null,
      category: product?.category ?? null,
    });

    return return_object;
  } catch (error) {
    logError('product_client', 'updateProductStock failed', {
      productId,
      status: error?.response?.status ?? null,
      data: error?.response?.data ?? null,
    });
    return Object.values(error.response.data);
  }
};

/**
 * Create a stock item in the backend.
 * @async
 * @param {string} token - The token for authentication.
 * @param {string} productId - The ID of the product for which the stock item needs to be created.
 * @param {object|array} data - The data for the stock item. It can be an object for a single stock item or an array of objects for multiple stock items.
 * @param {boolean} multiple - Indicates whether to create multiple stock items or not.
 * @param {object} product - The product object (optional, used for multi-component logic).
 * @returns {object} - An object with the following properties:
 *     - success {boolean} - Indicates whether the stock item creation was successful or not.
 *     - data {array} - An array of created stock items.
 *     - toast {function} - A function to show success toast message.
 * @throws {array} - An array containing the error messages if there are any errors during stock item creation.
 */
/**
 * Place fields for a lot. Omitted when the form did not offer a place, so a
 * batch edit cannot clear a location the row never loaded.
 *
 * @param {Object} itemData
 * @returns {{ storage_node_id: *, slot_index: * }|null}
 */
function stockItemPlaceFields(itemData) {
  if (!itemData || !Object.prototype.hasOwnProperty.call(itemData, 'storageNodeId')) {
    return null;
  }
  return {
    storage_node_id: itemData.storageNodeId ?? null,
    slot_index: itemData.slotIndex ?? null,
  };
}

export const createStockItem = async (
  token,
  productId,
  data,
  multiple,
  product = null
) => {
  let URL = `${BACKEND_URL}products/create_stock_item/`;

  if (multiple) URL += '?multiple=True';

  const place = multiple ? null : stockItemPlaceFields(data);
  const dataToSend = multiple
    ? { product_id: productId, items: data }
    : {
        product_id: productId,
        batch: data.batch,
        opened_by_name: data.openedBy,
        associated_user: data.associatedUserId ?? undefined,
        in_use: data.inUse,
        expiry: data.expiry,
        opened_on: data.openedOn,
        ...(place || {}),
      };

  const postItems = async body =>
    axios.post(URL, body, {
      headers: {
        Authorization: `Token ${token}`,
      },
    });

  try {
    const response = await postItems(dataToSend);

    // Note: Component stock items are now created automatically by the backend
    // when creating master stock items, so no frontend action is needed

    // Return the success status and a function to show success toast
    return {
      success: true,
      data: response.data['item(s)'],
      placementDropped: false,
      toast: () =>
        showToast('Stock item created successfully!', 'success', 'top-right', 3000),
    };

    // Catch any errors during update and return them
  } catch (error) {
    // Error handled silently
    return Object.values(error.response.data);
  }
};

/**
 * Updates a stock item.
 *
 * @async
 * @param {string} token - The user token used for authorization.
 * @param {string} itemId - The ID of the stock item to update.
 * @param {object} itemData - The new data for the stock item.
 * @param {string} itemData.batch - The new batch number for the stock item.
 * @param {boolean} itemData.inUse - The new in use status for the stock item.
 * @param {string} itemData.expiry - The new expiry date for the stock item.
 * @returns {Promise} A Promise that resolves to an object containing the success status and a function to show a success toast message in the UI, or rejects with an array of error messages
 *.
 */
export const updateStockItem = async (token, itemId, itemData) => {
  const place = stockItemPlaceFields(itemData);
  const body = {
    item_id: itemId,
    batch: itemData.batch,
    opened_by_name: itemData.openedBy,
    associated_user: itemData.associatedUserId ?? undefined,
    in_use: itemData.inUse,
    expiry: itemData.expiry,
    opened_on: itemData.openedOn,
    ...(place || {}),
  };
  const patchItem = payload =>
    axios.patch(`${BACKEND_URL}products/update_stock_item/`, payload, {
      headers: {
        Authorization: `Token ${token}`,
      },
    });
  try {
    const response = await patchItem(body);

    // Return the success status and a function to show success toast
    return {
      success: true,
      data: response.data.item,
      placementDropped: false,
      toast: () =>
        showToast('Stock item updated successfully!', 'success', 'top-right', 3000),
    };

    // Catch any errors during update and return them
  } catch (error) {
    // Error handled silently
    return Object.values(error.response.data);
  }
};

/**
 * Deletes a stock item from the backend.
 *
 * @param {string} token - The authentication token.
 * @param {string} itemId - The ID of the stock item to delete.
 * @param {object} product - The product object (optional, used for multi-component logic).
 * @param {object} stockItem - The stock item object (optional, used for multi-component logic).
 * @returns {Promise<Object>} - A promise that resolves to an object with the following properties:
 *   - success {boolean}: Indicates whether the deletion was successful.
 *   - toast {Function}: A function to show a success toast message.
 * @throws {Error} - If an error occurs during the deletion process.
 */
export const deleteStockItem = async (
  token,
  itemId,
  product = null,
  stockItem = null
) => {
  try {
    // Check if this is a multi-component product and delete component stock items first
    if (product && product.supports_multi_components && stockItem) {
      try {
        await deleteMultiComponentStockItems(token, stockItem, product);
      } catch (componentError) {
        // Error handled silently
        // Don't fail the main operation, just log the error
      }
    }

    // Make a delete request to the backend to delete a stock item
    await axios.delete(
      `${BACKEND_URL}products/delete_stock_item/?item_id=${itemId}`,
      {
        headers: {
          Authorization: `Token ${token}`,
        },
      }
    );

    // Return the success status and a function to show success toast
    return {
      success: true,
      toast: () =>
        showToast(
          'Stock item deleted successfully!',
          'success',
          'top-right',
          3000
        ),
    };

    // Catch any errors during update and return them
  } catch (error) {
    // Error handled silently
    return Object.values(error.response.data);
  }
};

/**
 * Update product item stock by making a PATCH request to the backend.
 * @async
 * @param {string} token - The authorization token.
 * @param {number} itemId - The item ID to update.
 * @param {number} updatedStock - The updated stock quantity.
 * @returns {Promise<Object>} - A promise that resolves to an object with the success status and a function to show success toast, or an array of error messages if there are any errors during update.
 */
export const updateProductItemStock = async (token, itemId, updatedStock) => {
  try {
    // Make a PATCH request to the backend to update the stock of the product item
    const response = await axios.patch(
      `${BACKEND_URL}products/update_stock_item_sub_stock/`,
      {
        item_id: itemId,
        updated_stock: updatedStock,
      },
      {
        headers: {
          Authorization: `Token ${token}`,
        },
      }
    );

    // Return the success status and a function to show success toast
    return {
      success: true,
      data: response.data.item,
      toast: () =>
        showToast(
          'Product item stock updated successfully!',
          'success',
          'top-right',
          3000
        ),
    };

    // Catch any errors during update and return them
  } catch (error) {
    // Error handled silently
    return Object.values(error.response.data);
  }
};

/**
 * Asynchronously fetches the logs associated with a specific product from the backend.
 *
 * @param {string} token - The authentication token required for the request.
 * @param {string} productId - The unique identifier of the product.
 * @returns {Promise<{ success: boolean, data: Array }>} - A promise that resolves to an object containing
 * the success status (true if successful) and an array of product logs if successful.
 * If there is an error during the fetch, the promise will be rejected with an array of error messages.
 */
export const fetchProductLogs = async (token, productId) => {
  try {
    // Make a PATCH request to the backend to update the stock of the product item
    const response = await axios.get(
      `${BACKEND_URL}products/fetch_product_logs/?product_id=${productId}`,

      {
        headers: {
          Authorization: `Token ${token}`,
        },
      }
    );

    // Return the success status and a function to show success toast
    return {
      success: true,
      data: response.data.logs,
    };

    // Catch any errors during update and return them
  } catch (error) {
    // Error handled silently
    return Object.values(error.response.data);
  }
};

/**
 * Deletes a product stock-item log (`DELETE products/delete_product_log/`).
 *
 * @param {string} token
 * @param {{ log_id?: number|string, stock_item_id?: number|string }} queryParams
 * @returns {Promise<{success: boolean, toast?: Function, errorMessage?: string}>}
 */
export const deleteProductLog = async (token, queryParams) => {
  const logId = queryParams?.log_id;
  const stockItemId = queryParams?.stock_item_id;
  if (
    (logId == null || logId === '') &&
    (stockItemId == null || stockItemId === '')
  ) {
    logError('product_client', 'deleteProductLog aborted: missing id', {
      queryParams: queryParams ?? null,
    });
    return { success: false, errorMessage: 'Missing log id' };
  }

  try {
    logInfo('product_client', 'deleteProductLog start', {
      logId: logId ?? null,
      stockItemId: stockItemId ?? null,
    });
    await axios.delete(`${BACKEND_URL}products/delete_product_log/`, {
      headers: {
        Authorization: `Token ${token}`,
      },
      params:
        logId != null && logId !== ''
          ? { log_id: logId }
          : { stock_item_id: stockItemId },
    });

    logInfo('product_client', 'deleteProductLog success', {
      logId: logId ?? null,
      stockItemId: stockItemId ?? null,
    });
    return {
      success: true,
      toast: () =>
        showToast(
          'Product log deleted successfully!',
          'success',
          'top-right',
          3000
        ),
    };
  } catch (error) {
    logError('product_client', 'deleteProductLog failed', {
      message: error.message,
      status: error.response?.status,
      data: error.response?.data,
    });
    return {
      success: false,
      errorMessage:
        error.response?.data?.detail ||
        error.response?.data?.error ||
        'Failed to delete product log',
    };
  }
};

/**
 * Get available components for kit products (non-kit products only).
 *
 * @async
 * @param {string} token - The access token for the current user.
 * @returns {Promise<Array>} - Array of available component products.
 * @throws Will throw an error if the request fails.
 */
export const getAvailableKitComponents = async token => {
  try {
    const response = await axios.get(
      `${BACKEND_URL}products/available_kit_components/`,
      {
        headers: {
          Authorization: `Token ${token}`,
        },
      }
    );

    return response.data;
  } catch (error) {
    // Error handled silently
    throw error;
  }
};

/**
 * Get available products for multi-component selection.
 * Returns all products that can be used as components in multi-component products.
 *
 * @async
 * @param {string} token - The access token for the current user.
 * @returns {Promise<Array>} - Array of available products for multi-component selection.
 * @throws Will throw an error if the request fails.
 */
export const getAvailableMultiComponentProducts = async token => {
  try {
    const response = await axios.get(`${BACKEND_URL}products/names/`, {
      headers: {
        Authorization: `Token ${token}`,
      },
    });

    return response.data;
  } catch (error) {
    // Error handled silently
    throw error;
  }
};

/**
 * Add a component to a kit product.
 *
 * @async
 * @param {string} token - The access token for the current user.
 * @param {number} kitId - The ID of the kit product.
 * @param {Object} componentData - The component data to add.
 * @param {number} componentData.component_id - The ID of the component product.
 * @param {number} componentData.quantity_per_kit - Quantity of this component per kit.
 * @param {boolean} componentData.is_required - Whether this component is required.
 * @returns {Promise<Object>} - The added component data.
 * @throws Will throw an error if the request fails.
 */
export const addKitComponent = async (token, kitId, componentData) => {
  try {
    const response = await axios.post(
      `${BACKEND_URL}products/${kitId}/add_kit_component/`,
      componentData,
      {
        headers: {
          Authorization: `Token ${token}`,
        },
      }
    );

    return response.data;
  } catch (error) {
    // Error handled silently
    throw error;
  }
};

/**
 * Remove a component from a kit product.
 *
 * @async
 * @param {string} token - The access token for the current user.
 * @param {number} kitId - The ID of the kit product.
 * @param {number} componentId - The ID of the component to remove.
 * @returns {Promise<Object>} - Success response.
 * @throws Will throw an error if the request fails.
 */
export const removeKitComponent = async (token, kitId, componentId) => {
  try {
    const response = await axios.delete(
      `${BACKEND_URL}products/${kitId}/remove_kit_component/${componentId}/`,
      {
        headers: {
          Authorization: `Token ${token}`,
        },
      }
    );

    return response.data;
  } catch (error) {
    // Error handled silently
    throw error;
  }
};

/**
 * Update a component in a kit product.
 *
 * @async
 * @param {string} token - The access token for the current user.
 * @param {number} kitId - The ID of the kit product.
 * @param {number} componentId - The ID of the component to update.
 * @param {Object} componentData - The updated component data.
 * @param {number} componentData.quantity_per_kit - Quantity of this component per kit.
 * @param {boolean} componentData.is_required - Whether this component is required.
 * @returns {Promise<Object>} - The updated component data.
 * @throws Will throw an error if the request fails.
 */
export const updateKitComponent = async (
  token,
  kitId,
  componentId,
  componentData
) => {
  try {
    const response = await axios.put(
      `${BACKEND_URL}products/${kitId}/update_kit_component/${componentId}/`,
      componentData,
      {
        headers: {
          Authorization: `Token ${token}`,
        },
      }
    );

    return response.data;
  } catch (error) {
    // Error handled silently
    throw error;
  }
};

/**
 * Get kit status for a product.
 *
 * @async
 * @param {string} token - The access token for the current user.
 * @param {number} productId - The ID of the product.
 * @returns {Promise<Object>} - Kit status information.
 * @throws Will throw an error if the request fails.
 */
export const getKitStatus = async (token, productId) => {
  try {
    const response = await axios.get(
      `${BACKEND_URL}products/${productId}/kit_status/`,
      {
        headers: {
          Authorization: `Token ${token}`,
        },
      }
    );

    return response.data;
  } catch (error) {
    // Error handled silently
    throw error;
  }
};

/**
 * Get all kit products.
 *
 * @async
 * @param {string} token - The access token for the current user.
 * @returns {Promise<Array>} - Array of kit products.
 * @throws Will throw an error if the request fails.
 */
export const getKitProducts = async token => {
  try {
    const response = await axios.get(`${BACKEND_URL}kit_products/`, {
      headers: {
        Authorization: `Token ${token}`,
      },
    });

    return response.data;
  } catch (error) {
    // Error handled silently
    throw error;
  }
};

/**
 * Create stock items for multi-component product components.
 * This function creates stock items for each component of a multi-component product
 * when a stock item is created for the main product.
 *
 * @async
 * @param {string} token - The access token for the current user.
 * @param {Array} createdStockItems - The stock items that were created for the main product.
 * @param {Object} product - The multi-component product object.
 * @returns {Promise<void>}
 * @throws Will throw an error if the request fails.
 */
export const createMultiComponentStockItems = async (
  token,
  createdStockItems,
  product
) => {
  // Ensure createdStockItems is an array
  const stockItemsArray = Array.isArray(createdStockItems)
    ? createdStockItems
    : [createdStockItems];

  if (
    !product.multi_component_status?.components ||
    stockItemsArray.length === 0
  ) {
    return;
  }

  const componentPromises = [];

  // For each created stock item of the main product
  for (const stockItem of stockItemsArray) {
    // For each component of the multi-component product
    for (const component of product.multi_component_status.components) {
      // Create stock items for the component based on quantity_per_component
      for (let i = 0; i < component.quantity_per_component; i++) {
        const componentStockItemData = {
          batch: stockItem.batch || '',
          openedBy: stockItem.opened_by_name || '',
          inUse: false, // Components start as not in use
          expiry: stockItem.expiry || null,
          openedOn: null, // Components start as not opened
        };

        componentPromises.push(
          createStockItem(
            token,
            component.id,
            componentStockItemData,
            false,
            null
          ) // Pass null as product to prevent recursive multi-component creation
        );
      }
    }
  }

  try {
    await Promise.all(componentPromises);
  } catch (error) {
    // Error handled silently
    throw error;
  }
};

/**
 * Delete stock items for multi-component product components.
 * This function deletes stock items for each component of a multi-component product
 * when a stock item is deleted for the main product.
 *
 * @async
 * @param {string} token - The access token for the current user.
 * @param {Object} deletedStockItem - The stock item that was deleted for the main product.
 * @param {Object} product - The multi-component product object.
 * @returns {Promise<void>}
 * @throws Will throw an error if the request fails.
 */
export const deleteMultiComponentStockItems = async (
  token,
  deletedStockItem,
  product
) => {
  if (!product.multi_component_status?.components || !deletedStockItem) {
    return;
  }

  try {
    // Call the backend to delete component stock items linked to this parent stock item
    await axios.delete(
      `${BACKEND_URL}stock_items/${deletedStockItem.id}/delete_component_stock_items/`,
      {
        headers: {
          Authorization: `Token ${token}`,
        },
      }
    );
  } catch (error) {
    // Error handled silently
    // Don't throw error to avoid failing the main deletion
  }
};

/**
 * Handles multi-component stock increase by creating component stock items
 * @param {string} token - Authentication token
 * @param {Object} product - Product object with multi-component data
 * @param {number} stockIncrease - Number of stock items to increase
 */
export const handleMultiComponentStockIncrease = async (
  token,
  product,
  stockIncrease
) => {
  // handleMultiComponentStockIncrease called

  if (!product.multi_component_status?.components || stockIncrease <= 0) {
    return;
  }

  const componentPromises = [];

  // For each component, create stock items based on quantity_per_component * stockIncrease
  for (const component of product.multi_component_status.components) {
    const totalComponentItems =
      component.quantity_per_component * stockIncrease;

    for (let i = 0; i < totalComponentItems; i++) {
      const componentStockItemData = {
        batch: '',
        openedBy: '',
        inUse: false,
        expiry: null,
        openedOn: null,
      };

      // Creating component stock item for component

      componentPromises.push(
        createStockItem(
          token,
          component.id,
          componentStockItemData,
          false,
          null
        )
      );
    }
  }

  try {
    await Promise.all(componentPromises);
    // Created component stock items for multi-component product
  } catch (error) {
    logError('product_client', 'handleMultiComponentStockIncrease component stock create failed', error);
    throw error;
  }
};

/**
 * Handles multi-component stock decrease by deleting component stock items
 * @param {string} token - Authentication token
 * @param {Object} product - Product object with multi-component data
 * @param {number} stockDecrease - Number of stock items to decrease
 */
export const handleMultiComponentStockDecrease = async (
  token,
  product,
  stockDecrease
) => {
  if (!product.multi_component_status?.components || stockDecrease <= 0) {
    return;
  }

  try {
    // For each component, we need to delete stock items
    // We'll fetch the component's stock items and delete the oldest ones

    for (const component of product.multi_component_status.components) {
      const totalComponentItemsToDelete =
        component.quantity_per_component * stockDecrease;

      // Since we can't reliably fetch component stock items due to permissions,
      // we'll use a simpler approach: just log the intended deletion

      // For now, we'll just log what should be deleted
      // The actual deletion logic would need to be implemented on the backend
      // or we'd need to find a different approach to access component stock items

      if (component.component_stock < totalComponentItemsToDelete) {
      }
    }
  } catch (error) {
    // Error handled silently
    // Don't throw error to avoid failing the main operation
  }
};

/**
 * Get detailed kit information for a specific kit product.
 *
 * @async
 * @param {string} token - The access token for the current user.
 * @param {number} kitId - The ID of the kit product.
 * @returns {Promise<Object>} - Detailed kit information.
 * @throws Will throw an error if the request fails.
 */
export const getKitProductDetails = async (token, kitId) => {
  try {
    const response = await axios.get(`${BACKEND_URL}kit_products/${kitId}/`, {
      headers: {
        Authorization: `Token ${token}`,
      },
    });

    return response.data;
  } catch (error) {
    logError('product_client', 'getKitProductDetails failed', error);
    throw error;
  }
};
